/**
 * TDR Research Layer Page
 *
 * /labs/tradingview-indicator-tdr — Public page positioning TDR as a research layer for TradingView,
 * not just an indicator. Visitors submit email + TradingView username for access.
 * Form data captured via MailerLite.
 */

import { type FC, useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine,
  faShieldAlt,
  faBolt,
  faCheck,
  faArrowRight,
  faSpinner,
  faExclamationTriangle,
  faDatabase,
  faCompass,
  faFlask,
  faCrosshairs,
  faMagnifyingGlassChart,
  faUserCheck,
} from '@fortawesome/free-solid-svg-icons';
import PublicNavBar from '../../components/layout/PublicNavBar';
import PublicFooter from '../../components/layout/PublicFooter';
import { usePageMeta } from '../../hooks/usePageMeta';

/* ── API config ── */
const API_BASE = import.meta.env.VITE_API_URL || '/api';

/* ── Scroll reveal hook ── */
function useScrollReveal(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return {
    ref,
    visible,
    style: {
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(18px)',
      transition: 'opacity 0.7s ease, transform 0.7s ease',
    } as React.CSSProperties,
  };
}

/* ── Three layers ── */
const LAYERS = [
  {
    icon: faBolt,
    title: 'Signal Layer',
    bullets: ['Entries, exits, trend state', 'Confirmed on candle close', 'No repainting, no noise'],
  },
  {
    icon: faDatabase,
    title: 'Data Layer',
    bullets: ['Win rate, avg trade, drawdown', 'Trade count and distribution', 'Performance visible on-chart'],
  },
  {
    icon: faShieldAlt,
    title: 'Structure Layer',
    bullets: ['Risk zones and stop levels', 'Take-profit and entry areas', 'Trade planning on the chart'],
  },
];

/* ── Feature cards ── */
const FEATURES = [
  {
    icon: faChartLine,
    title: 'Adaptive Trend Engine',
    description: 'Filters noise and highlights high-conviction market states using momentum-based logic. Not a moving average overlay.',
  },
  {
    icon: faCrosshairs,
    title: 'Execution Signals (No Repainting)',
    description: 'Generated only on confirmed candle closes. What you see is what existed in real time. No after-the-fact adjustments.',
  },
  {
    icon: faShieldAlt,
    title: 'Structured Risk Zones',
    description: 'Plots risk zones, take-profit levels, and low-risk entries directly on your chart. Every trade has defined parameters.',
  },
  {
    icon: faFlask,
    title: 'Strategy Logic from Quanthop',
    description: 'Built using the same computation engine that powers Quanthop\'s backtesting and walk-forward validation workflows.',
  },
];

/* ── Metrics ── */
const MOCK_PERFORMANCE = [
  { label: 'Win Rate', value: '61.4%', color: 'text-emerald-400' },
  { label: 'Total Trades', value: '247', color: 'text-gray-200' },
  { label: 'Avg Trade', value: '+1.83%', color: 'text-emerald-400' },
  { label: 'Max Drawdown', value: '-8.7%', color: 'text-red-400' },
  { label: 'Risk/Reward', value: '2.14', color: 'text-emerald-400' },
  { label: 'Compound Return', value: '+312.6%', color: 'text-emerald-400' },
  { label: 'Profit Factor', value: '1.89', color: 'text-gray-200' },
  { label: 'Avg Win', value: '+3.91%', color: 'text-emerald-400' },
  { label: 'Avg Loss', value: '-1.82%', color: 'text-red-400' },
  { label: 'Win Streak', value: '7', color: 'text-gray-200' },
];

const MOCK_SIGNAL = [
  { label: 'Trend', value: 'BULLISH', color: 'text-emerald-400' },
  { label: 'Signal', value: 'LONG ACTIVE', color: 'text-emerald-400' },
  { label: 'Strength', value: 'HIGH', color: 'text-emerald-400' },
  { label: 'Entry', value: '64,218.40', color: 'text-gray-200' },
  { label: 'Stop Loss', value: '62,891.10', color: 'text-red-400' },
  { label: 'Take Profit', value: '67,340.80', color: 'text-emerald-400' },
  { label: 'Risk Zone', value: 'LOW RISK', color: 'text-emerald-400' },
  { label: 'Risk %', value: '2.07%', color: 'text-gray-200' },
];

/* ── Who this is for ── */
const AUDIENCE = [
  { icon: faMagnifyingGlassChart, text: 'Traders who want structure on their charts, not just arrows' },
  { icon: faFlask, text: 'People building or validating a strategy before committing capital' },
  { icon: faCompass, text: 'Anyone transitioning from discretionary to systematic trading' },
  { icon: faUserCheck, text: 'Users exploring Quanthop who want a visual preview of the research logic' },
];

/* ── Workflow steps ── */
const WORKFLOW = [
  { step: '1', title: 'Explore on chart', desc: 'See how TDR identifies trends, risk zones, and trade structure in real time.' },
  { step: '2', title: 'Validate in Quanthop', desc: 'Run the same logic through backtesting with full performance data.' },
  { step: '3', title: 'Optimise and stress test', desc: 'Parameter optimisation, walk-forward analysis, Monte Carlo simulation.' },
  { step: '4', title: 'Deploy with confidence', desc: 'Move from research to execution with validated, data-backed decisions.' },
];

/* ── Form states ── */
type FormState = 'idle' | 'submitting' | 'success' | 'error';

const TdrIndicatorPage: FC = () => {
  usePageMeta({ title: 'TDR Indicator for TradingView', description: 'Turn TradingView into a research terminal. Non-repainting signals, on-chart performance data, and structured risk zones powered by Quanthop.' });
  const [email, setEmail] = useState('');
  const [tvUsername, setTvUsername] = useState('');
  const [formState, setFormState] = useState<FormState>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const revLayers = useScrollReveal(0.1);
  const revPreview = useScrollReveal(0.1);
  const revMetrics = useScrollReveal(0.1);
  const revFeatures = useScrollReveal(0.1);
  const revAudience = useScrollReveal(0.15);
  const revWorkflow = useScrollReveal(0.15);
  const revAccess = useScrollReveal(0.15);
  const revCta = useScrollReveal(0.15);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');

    if (!email.trim() || !tvUsername.trim()) {
      setErrorMessage('Please fill in both fields.');
      return;
    }

    setFormState('submitting');

    try {
      const response = await fetch(`${API_BASE}/labs/tdr-signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), tvUsername: tvUsername.trim() }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Submission failed');
      }

      setFormState('success');
    } catch {
      setFormState('error');
      setErrorMessage('Something went wrong. Please try again.');
    }
  };

  /* ── Reusable signup form ── */
  const renderForm = (id?: string) => (
    formState === 'success' ? (
      <div className="max-w-md mx-auto rounded-lg border border-emerald-400/20 bg-emerald-400/[0.06] p-6 text-center">
        <div className="w-9 h-9 rounded-md bg-emerald-400/10 flex items-center justify-center mx-auto mb-3">
          <FontAwesomeIcon icon={faCheck} className="text-[14px] text-emerald-400" />
        </div>
        <h3 className="text-[15px] font-semibold text-emerald-300 mb-2">Access requested</h3>
        <p className="text-[13px] text-gray-400/85">
          Check your inbox for setup instructions. We'll grant access to your TradingView account, usually within hours.
        </p>
      </div>
    ) : (
      <form onSubmit={handleSubmit} className="max-w-md mx-auto" id={id}>
        <div className="flex flex-col gap-3">
          <input
            type="email"
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full px-4 py-2.5 rounded-lg bg-gray-900/50 border border-gray-800 text-white text-[14px] placeholder-gray-600 focus:outline-none focus:border-[#9d4edd]/40 transition-colors"
          />
          <input
            type="text"
            placeholder="TradingView username"
            value={tvUsername}
            onChange={(e) => setTvUsername(e.target.value)}
            required
            className="w-full px-4 py-2.5 rounded-lg bg-gray-900/50 border border-gray-800 text-white text-[14px] placeholder-gray-600 focus:outline-none focus:border-[#9d4edd]/40 transition-colors"
          />
          {errorMessage && (
            <div className="flex items-center gap-2 text-[12px] text-red-400">
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-[11px]" />
              {errorMessage}
            </div>
          )}
          <button
            type="submit"
            disabled={formState === 'submitting'}
            className="w-full px-4 py-2.5 rounded-lg bg-[#9d4edd] hover:bg-[#8b3ec7] disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-medium flex items-center justify-center gap-2 transition-colors"
          >
            {formState === 'submitting' ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="text-sm animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                Get Free Access to TDR
                <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
              </>
            )}
          </button>
        </div>
        <p className="text-[11px] font-mono text-gray-600 mt-3">
          No spam. No nonsense. Unsubscribe anytime.
        </p>
      </form>
    )
  );

  return (
    <div className="min-h-screen bg-[#0a0e14] text-gray-100 font-[Poppins] selection:bg-[#9d4edd]/30 flex flex-col">
      <PublicNavBar />

      {/* ── 1. Hero ── */}
      <section className="relative overflow-hidden pt-32 pb-24 px-4 sm:px-6">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-16 left-1/2 -translate-x-1/2 w-[600px] h-[350px] rounded-full bg-[#9d4edd]/[0.035] blur-[160px]" />
        </div>

        <div className="relative max-w-[900px] mx-auto text-center">
          <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#9d4edd]/40 mb-6">
            Quanthop Labs
          </div>
          <h1 className="text-3xl md:text-[42px] font-bold text-white tracking-[-0.02em] leading-[1.15] mb-5">
            Turn TradingView into a{' '}
            <span className="text-[#9d4edd]">Research Terminal</span>
          </h1>

          <p className="text-[15px] text-gray-300/85 leading-[1.7] max-w-[560px] mx-auto mb-6">
            TDR layers structured signals, performance metrics, and risk zones
            directly onto your charts. Not just another indicator —
            a research framework you can see.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[13px] text-gray-400/80 mb-10">
            {['Non-repainting signals', 'On-chart performance data', 'Defined risk zones'].map((t) => (
              <span key={t} className="flex items-center gap-2">
                <span className="w-1 h-1 rounded-full bg-[#9d4edd]/30" />
                {t}
              </span>
            ))}
          </div>

          {renderForm('hero-form')}
        </div>
      </section>

      {/* ── 2. Not Just Signals ── */}
      <section className="py-24 px-4 sm:px-6 border-t border-gray-700/40" ref={revLayers.ref} style={revLayers.style}>
        <div className="max-w-[900px] mx-auto">
          <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#9d4edd]/40 text-center mb-4">
            Not just signals
          </div>
          <h2 className="text-[22px] font-semibold text-white text-center tracking-[-0.02em] mb-3">
            A Research Framework. Not a Normal Indicator.
          </h2>
          <p className="text-[14px] text-gray-400/80 text-center mb-12 max-w-[560px] mx-auto">
            Most indicators give you arrows. TDR gives you three layers of structured research data on every chart.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {LAYERS.map((l) => (
              <div
                key={l.title}
                className="rounded-lg border border-white/[0.06] px-5 py-5 hover:border-[#9d4edd]/25 transition-all"
                style={{ background: 'rgba(157, 78, 221, 0.015)' }}
              >
                <div className="w-9 h-9 rounded-md bg-[#9d4edd]/[0.08] flex items-center justify-center mb-4">
                  <FontAwesomeIcon icon={l.icon} className="text-[14px] text-[#9d4edd]/70" />
                </div>
                <h3 className="text-[14px] font-semibold text-white mb-3">{l.title}</h3>
                <ul className="space-y-2">
                  {l.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-[13px] text-gray-400/85 leading-[1.6]">
                      <span className="w-1 h-1 rounded-full bg-[#9d4edd]/30 mt-[7px] shrink-0" />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 3. Chart Preview ── */}
      <section className="py-20 px-4 sm:px-6 border-t border-gray-700/40" ref={revPreview.ref} style={revPreview.style}>
        <div className="max-w-[1100px] mx-auto">
          <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#9d4edd]/40 text-center mb-4">
            On your chart
          </div>
          <h2 className="text-[22px] font-semibold text-white text-center tracking-[-0.02em] mb-10">
            See Strategy Performance Without Leaving Your Chart
          </h2>
          <div className="rounded-lg overflow-hidden shadow-2xl shadow-black/40" style={{ border: '10px solid rgba(255,255,255,0.3)' }}>
            <img
              src="/labs/tdr-chart-preview.png"
              alt="TDR indicator on a TradingView chart showing trend signals, risk zones, and performance metrics"
              className="w-full h-auto block"
            />
          </div>
        </div>
      </section>

      {/* ── 4. Metrics Showcase — Mock Data Panels ── */}
      <section className="py-24 px-4 sm:px-6 border-t border-gray-700/40" ref={revMetrics.ref} style={revMetrics.style}>
        <div className="max-w-[900px] mx-auto">
          <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#9d4edd]/40 text-center mb-4">
            Built-in performance
          </div>
          <h2 className="text-[22px] font-semibold text-white text-center tracking-[-0.02em] mb-3">
            Performance Data. Directly on Chart.
          </h2>
          <p className="text-[14px] text-gray-400/80 text-center mb-12 max-w-[540px] mx-auto">
            TDR displays live strategy metrics on the chart itself. No need to switch tabs,
            export spreadsheets, or run a separate backtest.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Strategy Performance panel */}
            <div className="rounded-lg border border-white/[0.06] overflow-hidden" style={{ background: 'rgba(10, 14, 20, 0.95)' }}>
              <div className="px-4 py-2.5 border-b border-white/[0.06] flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-[#9d4edd]/60" />
                <span className="text-[11px] font-mono tracking-wide uppercase text-gray-400/70">Strategy Performance</span>
              </div>
              <div className="px-4 py-3">
                {MOCK_PERFORMANCE.map((m) => (
                  <div key={m.label} className="flex items-center justify-between py-[5px] border-b border-white/[0.03] last:border-0">
                    <span className="text-[12px] font-mono text-gray-500">{m.label}</span>
                    <span className={`text-[12px] font-mono font-medium ${m.color}`}>{m.value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Signal State panel */}
            <div className="rounded-lg border border-white/[0.06] overflow-hidden" style={{ background: 'rgba(10, 14, 20, 0.95)' }}>
              <div className="px-4 py-2.5 border-b border-white/[0.06] flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400/60" />
                <span className="text-[11px] font-mono tracking-wide uppercase text-gray-400/70">Current Signal</span>
              </div>
              <div className="px-4 py-3">
                {MOCK_SIGNAL.map((m) => (
                  <div key={m.label} className="flex items-center justify-between py-[5px] border-b border-white/[0.03] last:border-0">
                    <span className="text-[12px] font-mono text-gray-500">{m.label}</span>
                    <span className={`text-[12px] font-mono font-medium ${m.color}`}>{m.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <p className="text-[12px] text-gray-500/60 text-center mt-6">
            Example data for illustration. All metrics calculated from confirmed candle data. No lookahead. No repainting.
          </p>
        </div>
      </section>

      {/* ── 5. Feature Cards ── */}
      <section className="py-24 px-4 sm:px-6 border-t border-gray-700/40" ref={revFeatures.ref} style={revFeatures.style}>
        <div className="max-w-[900px] mx-auto">
          <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#9d4edd]/40 text-center mb-4">
            Under the hood
          </div>
          <h2 className="text-[22px] font-semibold text-white text-center tracking-[-0.02em] mb-3">
            Built for Research, Not Hype
          </h2>
          <p className="text-[14px] text-gray-400/80 text-center mb-12 max-w-[500px] mx-auto">
            Every part of TDR exists because it solves a specific problem in systematic trading research.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {FEATURES.map((f) => (
              <div
                key={f.title}
                className="rounded-lg border border-white/[0.06] px-5 py-5 hover:border-[#9d4edd]/25 transition-all"
                style={{ background: 'rgba(157, 78, 221, 0.015)' }}
              >
                <div className="w-9 h-9 rounded-md bg-[#9d4edd]/[0.08] flex items-center justify-center mb-4">
                  <FontAwesomeIcon icon={f.icon} className="text-[14px] text-[#9d4edd]/70" />
                </div>
                <h3 className="text-[14px] font-semibold text-white mb-2">{f.title}</h3>
                <p className="text-[13px] text-gray-400/85 leading-[1.7]">{f.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 6. Who This Is For ── */}
      <section className="py-24 px-4 sm:px-6 border-t border-gray-700/40" ref={revAudience.ref} style={revAudience.style}>
        <div className="max-w-[700px] mx-auto">
          <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#9d4edd]/40 text-center mb-4">
            Who it's for
          </div>
          <h2 className="text-[22px] font-semibold text-white text-center tracking-[-0.02em] mb-10">
            Built for Traders Who Want Structure
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {AUDIENCE.map((a) => (
              <div
                key={a.text}
                className="flex items-start gap-3.5 rounded-lg border border-white/[0.06] px-5 py-4"
                style={{ background: 'rgba(157, 78, 221, 0.015)' }}
              >
                <div className="w-8 h-8 rounded-md bg-[#9d4edd]/[0.08] flex items-center justify-center shrink-0 mt-0.5">
                  <FontAwesomeIcon icon={a.icon} className="text-[13px] text-[#9d4edd]/70" />
                </div>
                <p className="text-[13px] text-gray-300/90 leading-[1.65]">{a.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 7. Quanthop Connection ── */}
      <section className="py-24 px-4 sm:px-6 border-t border-gray-700/40" ref={revWorkflow.ref} style={revWorkflow.style}>
        <div className="max-w-[900px] mx-auto">
          <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#9d4edd]/40 text-center mb-4">
            The bigger picture
          </div>
          <h2 className="text-[22px] font-semibold text-white text-center tracking-[-0.02em] mb-3">
            From Chart Observation to Validated Strategy
          </h2>
          <p className="text-[14px] text-gray-400/80 text-center mb-12 max-w-[560px] mx-auto">
            TDR on TradingView is the starting point. Quanthop is where you go deeper.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-5">
            {WORKFLOW.map((w, i) => (
              <div key={w.step} className="relative text-center">
                <div className="w-8 h-8 rounded-full bg-[#9d4edd]/10 flex items-center justify-center mx-auto mb-4">
                  <span className="text-[12px] font-mono font-bold text-[#9d4edd]/70">{w.step}</span>
                </div>
                <h3 className="text-[14px] font-semibold text-white mb-2">{w.title}</h3>
                <p className="text-[13px] text-gray-400/85 leading-[1.7]">{w.desc}</p>
                {i < WORKFLOW.length - 1 && (
                  <div className="hidden md:block absolute top-4 left-[calc(50%+24px)] w-[calc(100%-48px)] border-t border-dashed border-gray-700/40" />
                )}
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <a
              href="/pricing"
              className="inline-flex items-center gap-2 text-[13px] font-medium text-[#9d4edd] hover:text-[#8b3ec7] transition-colors"
            >
              Explore the Quanthop research platform
              <FontAwesomeIcon icon={faArrowRight} className="text-[11px]" />
            </a>
          </div>
        </div>
      </section>

      {/* ── 8. Access Flow ── */}
      <section className="py-24 px-4 sm:px-6 border-t border-gray-700/40" ref={revAccess.ref} style={revAccess.style}>
        <div className="max-w-[700px] mx-auto">
          <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#9d4edd]/40 text-center mb-4">
            Getting started
          </div>
          <h2 className="text-[22px] font-semibold text-white text-center tracking-[-0.02em] mb-10">
            Free Access. No Credit Card.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {[
              { step: '1', title: 'Submit your details', desc: 'Enter your email and TradingView username below.' },
              { step: '2', title: 'We grant access', desc: 'Your TradingView account gets added, usually within hours.' },
              { step: '3', title: 'Add to your charts', desc: 'Search "TDR" in invite-only indicators on TradingView and apply to any chart.' },
            ].map((s) => (
              <div key={s.step} className="text-center">
                <div className="w-8 h-8 rounded-full bg-[#9d4edd]/10 flex items-center justify-center mx-auto mb-4">
                  <span className="text-[12px] font-mono font-bold text-[#9d4edd]/70">{s.step}</span>
                </div>
                <h3 className="text-[14px] font-semibold text-white mb-2">{s.title}</h3>
                <p className="text-[13px] text-gray-400/85 leading-[1.7]">{s.desc}</p>
              </div>
            ))}
          </div>
          {renderForm('access-form')}
        </div>
      </section>

      {/* ── 9. Final CTA ── */}
      <section className="py-20 px-4 sm:px-6 border-t border-gray-700/40" ref={revCta.ref} style={revCta.style}>
        <div className="max-w-[560px] mx-auto text-center">
          <h2 className="text-[22px] font-semibold text-white tracking-[-0.02em] mb-4">
            Stop Guessing. Start Researching Properly.
          </h2>
          <p className="text-[14px] text-gray-400/85 leading-[1.7] mb-8">
            TDR turns your TradingView charts into a structured research surface — with signals,
            risk zones, and performance data that actually mean something. Free access. No credit card.
          </p>
          <a
            href="#hero-form"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-[#9d4edd] hover:bg-[#8b3ec7] text-white text-sm font-medium transition-colors"
          >
            Get Free Access to TDR
            <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
          </a>
          <p className="text-[12px] text-gray-500/60 mt-4">
            For research and educational purposes. Not intended as a standalone trading system.
          </p>
        </div>
      </section>

      <div className="mt-auto">
        <PublicFooter />
      </div>
    </div>
  );
};

export default TdrIndicatorPage;
